import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import GameSetup from '../game/GameSetup';
import { getApiUrl } from '../../utils/api-url';
import { MagicParticles, AnimatedCard, AnimatedButton, AnimatedList } from '../effects';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useGameStore } from '../../store/gameStore';

// Default fallback worlds if no generated images exist
const DEFAULT_WORLDS = [
  {
    name: 'Star Wars',
    image: 'https://images.unsplash.com/photo-1579566346927-c68383817a25?w=1920&q=80',
    color: '#4a5568',
  },
  {
    name: 'Cyberpunk 2077',
    image: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1920&q=80',
    color: '#ec4899',
  },
  {
    name: 'Witcher',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1920&q=80',
    color: '#8b5cf6',
  },
  {
    name: 'Lord of the Rings',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
    color: '#10b981',
  },
  {
    name: 'Harry Potter',
    image: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1920&q=80',
    color: '#f59e0b',
  },
];

// Color mapping for universe types
const UNIVERSE_COLORS: Record<string, string> = {
  'Star Wars': '#4a5568',
  'Cyberpunk 2077': '#ec4899',
  'Witcher': '#8b5cf6',
  'Lord of the Rings': '#10b981',
  'Harry Potter': '#f59e0b',
  'Warhammer 40000': '#dc2626',
  'Dungeons & Dragons': '#f97316',
  'Mass Effect': '#3b82f6',
  'Game of Thrones': '#78716c',
};

export default function MagicalHome() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { draft, resumeDraft, discardDraft } = useGameStore();
  // Only show the "My Games" button once the user actually has games — created on this device
  // (persisted recentGames) or signed in (may have account games). A brand-new, never-invited
  // visitor has neither, so we hide it for them.
  const hasGames = (() => {
    try {
      const s = JSON.parse(localStorage.getItem('rpg-multiverse-game-store') || '{}');
      const recent = s?.state?.recentGames || [];
      const loggedIn = !!JSON.parse(localStorage.getItem('userData') || 'null')?.user?.id;
      return (Array.isArray(recent) && recent.length > 0) || loggedIn;
    } catch { return false; }
  })();
  const [currentWorld, setCurrentWorld] = useState(0);
  // Auto-rotation pauses while the reader is looking at a slide (after they swipe / tap a dot),
  // and resumes once they scroll the page — the signal that they're done reading.
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  // Open the create-game form immediately when arriving via /?create=1 (e.g. the button on /games).
  const [showSetup, setShowSetup] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('create') === '1'; } catch { return false; }
  });
  const [fantasyWorlds, setFantasyWorlds] = useState(DEFAULT_WORLDS);

  const apiUrl = getApiUrl();

  // Fetch generated world images
  useEffect(() => {
    const fetchWorldShowcase = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/games/worlds/showcase`);
        const data = await response.json();

        if (data.worlds && data.worlds.length > 0) {
          // Map generated worlds to the format we need
          const generatedWorlds = data.worlds.map((world: any) => ({
            name: world.universe,
            image: `${apiUrl}/api/media/${world.imageId}`,
            color: UNIVERSE_COLORS[world.universe] || '#667eea',
          }));

          // If we have at least 3 generated worlds, use them, otherwise mix with defaults
          if (generatedWorlds.length >= 3) {
            setFantasyWorlds(generatedWorlds);
          } else {
            // Mix generated with defaults to have at least 5 worlds
            const combined = [...generatedWorlds];
            const needed = 5 - generatedWorlds.length;
            const defaultsToAdd = DEFAULT_WORLDS.filter(
              (dw) => !generatedWorlds.some((gw: any) => gw.name === dw.name)
            ).slice(0, needed);
            setFantasyWorlds([...combined, ...defaultsToAdd]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch world showcase:', error);
        // Keep using default worlds on error
      }
    };

    fetchWorldShowcase();
  }, [apiUrl]);

  useEffect(() => {
    if (paused) return; // reader is on a slide — don't advance until they scroll on
    const interval = setInterval(() => {
      setCurrentWorld((prev) => (prev + 1) % fantasyWorlds.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [fantasyWorlds.length, paused]);

  // While paused, resume the carousel the moment the user scrolls the page (they've read enough).
  useEffect(() => {
    if (!paused) return;
    const resume = () => setPaused(false);
    window.addEventListener('scroll', resume, { passive: true, once: true });
    return () => window.removeEventListener('scroll', resume);
  }, [paused]);

  // Manual navigation stops the auto-advance so the slide stays put long enough to read.
  const goToWorld = (idx: number) => {
    const n = fantasyWorlds.length;
    if (n === 0) return;
    setCurrentWorld(((idx % n) + n) % n);
    setPaused(true);
  };

  const onShowcaseTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onShowcaseTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - (touchStartY.current ?? 0);
    // Horizontal swipe (and clearly more horizontal than vertical) = navigate; swipe right re-reads.
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      goToWorld(currentWorld + (dx < 0 ? 1 : -1));
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const world = fantasyWorlds[currentWorld];

  return (
    <div style={{ position: 'relative', minHeight: '80vh' }}>
      {/* Animated Background with World Images */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
      }}>
        {fantasyWorlds.map((w, idx) => (
          <div
            key={w.name + idx}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${w.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: idx === currentWorld ? 1 : 0,
              transition: 'opacity 2s ease-in-out',
              filter: 'brightness(0.4)',
            }}
          />
        ))}

        {/* Magical Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 50% 50%, transparent 0%, rgba(15, 12, 41, 0.9) 100%)`,
          animation: 'pulse 4s infinite',
        }} />

        {/* Magic Particles - Interactive */}
        <MagicParticles />
      </div>

      {!showSetup ? (
        /* Hero Section */
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: isMobile ? '3rem 1rem' : '6rem 2rem',
          textAlign: 'center',
        }}>
          {/* Resume an unfinished world the user backed out of during setup */}
          {draft?.game && (
            <div style={{
              marginBottom: '2rem',
              padding: isMobile ? '1rem' : '1.25rem 1.5rem',
              borderRadius: '16px',
              background: 'rgba(99, 102, 241, 0.12)',
              border: '1px solid rgba(99, 102, 241, 0.4)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
            }}>
              <span style={{ color: '#e2e8f0', fontSize: '0.95rem' }}>
                📝 {t('home.resumeDraft', { name: draft.game.name })}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  onClick={() => resumeDraft()}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff',
                    fontSize: '0.9rem', fontWeight: 600,
                  }}
                >
                  {t('home.resumeDraftButton')}
                </button>
                <button
                  onClick={() => discardDraft()}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '10px', cursor: 'pointer',
                    border: '1px solid rgba(239, 68, 68, 0.4)', background: 'rgba(239, 68, 68, 0.1)',
                    color: '#fca5a5', fontSize: '0.9rem',
                  }}
                >
                  {t('home.discardDraft')}
                </button>
              </div>
            </div>
          )}

          {/* Magical Title */}
          <div style={{
            marginBottom: '3rem',
            animation: 'fadeInDown 1s ease-out',
            minHeight: '180px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'visible',
          }}>
            <h1 style={{
              fontSize: isMobile ? '1.7rem' : 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 'bold',
              marginBottom: '1rem',
              minHeight: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              overflow: 'visible',
            }}>
              <span style={{ fontSize: '1em' }}>✨</span>
              <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 40px rgba(102, 126, 234, 0.5)',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
              }}>
                {t('home.welcome_title').replace(/✨/g, '').trim()}
              </span>
              <span style={{ fontSize: '1em' }}>✨</span>
            </h1>
            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
              color: '#e2e8f0',
              textShadow: '0 2px 10px rgba(0,0,0,0.5)',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.6',
              minHeight: '70px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {t('home.welcome_subtitle')}
            </p>
          </div>

          {/* World Showcase — swipe (or tap arrows/dots) to browse; auto-rotates until you interact */}
          <div
            onTouchStart={onShowcaseTouchStart}
            onTouchEnd={onShowcaseTouchEnd}
            style={{ position: 'relative', touchAction: 'pan-y' }}
          >
            <AnimatedCard variant="slideUp" delay={0.3} style={{
              background: 'rgba(15, 12, 41, 0.6)',
              backdropFilter: 'blur(20px)',
              padding: isMobile ? '1.25rem' : '2rem',
              borderRadius: '24px',
              border: '2px solid rgba(102, 126, 234, 0.3)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
              marginBottom: isMobile ? '1.5rem' : '3rem',
            }}>
              <h2 style={{
                fontSize: isMobile ? '1.3rem' : '1.8rem',
                color: world.color,
                marginBottom: '1rem',
                textShadow: `0 0 20px ${world.color}`,
                transition: 'all 1s',
              }}>
                {world.name}
              </h2>

              {/* World Indicators — clickable, so any slide is reachable directly */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
                marginTop: '1.5rem',
              }}>
                {fantasyWorlds.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToWorld(idx)}
                    aria-label={`${t('home.showWorld')} ${idx + 1}`}
                    style={{
                      border: 'none',
                      cursor: 'pointer',
                      padding: idx === currentWorld ? '0' : '0.35rem',
                      margin: idx === currentWorld ? '0.35rem 0' : '0',
                      background: 'transparent',
                    }}
                  >
                    <span style={{
                      display: 'block',
                      width: idx === currentWorld ? '32px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      background: idx === currentWorld ? world.color : 'rgba(255,255,255,0.3)',
                      transition: 'all 0.5s',
                      boxShadow: idx === currentWorld ? `0 0 10px ${world.color}` : 'none',
                    }} />
                  </button>
                ))}
              </div>
            </AnimatedCard>

            {/* Prev / Next arrows (mainly for desktop; mobile uses swipe) */}
            {['prev', 'next'].map((dir) => (
              <button
                key={dir}
                onClick={() => goToWorld(currentWorld + (dir === 'next' ? 1 : -1))}
                aria-label={dir === 'next' ? t('home.nextWorld') : t('home.prevWorld')}
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  [dir === 'next' ? 'right' : 'left']: isMobile ? '-2px' : '-14px',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: '1px solid rgba(102, 126, 234, 0.4)',
                  background: 'rgba(15, 12, 41, 0.7)',
                  color: '#e2e8f0',
                  fontSize: '1.2rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                } as React.CSSProperties}
              >
                {dir === 'next' ? '›' : '‹'}
              </button>
            ))}
          </div>

          {/* About Section */}
          <AnimatedCard variant="fadeIn" delay={0.4} style={{
            background: 'rgba(15, 12, 41, 0.6)',
            backdropFilter: 'blur(20px)',
            padding: isMobile ? '1.25rem' : '2rem',
            borderRadius: '24px',
            border: '2px solid rgba(102, 126, 234, 0.3)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
            marginBottom: isMobile ? '1.5rem' : '3rem',
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.15rem' : '1.8rem',
              color: '#e2e8f0',
              marginBottom: '1rem',
              textAlign: 'center',
            }}>
              {t('home.about_title')}
            </h2>
            <p style={{
              fontSize: isMobile ? '0.95rem' : '1.1rem',
              color: '#cbd5e0',
              lineHeight: '1.8',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto',
            }}>
              {t('home.about_desc')}
            </p>
          </AnimatedCard>

          {/* How It Works */}
          <AnimatedCard variant="scale" delay={0.5} style={{
            background: 'rgba(15, 12, 41, 0.6)',
            backdropFilter: 'blur(20px)',
            padding: isMobile ? '1.25rem' : '2rem',
            borderRadius: '24px',
            border: '2px solid rgba(102, 126, 234, 0.3)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            marginBottom: isMobile ? '1.5rem' : '3rem',
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.15rem' : '1.8rem',
              color: '#e2e8f0',
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}>
              {t('home.how_it_works_title')}
            </h2>
            <AnimatedList staggerDelay={0.1}>
              {[
                { num: 1, text: t('home.how_step1') },
                { num: 2, text: t('home.how_step2') },
                { num: 3, text: t('home.how_step3') },
                { num: 4, text: t('home.how_step4') },
                { num: 5, text: t('home.how_step5') },
              ].map((step) => (
                <div
                  key={step.num}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                  }}
                >
                  <div style={{
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}>
                    {step.num}
                  </div>
                  <p style={{
                    color: '#cbd5e0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    margin: 0,
                  }}>
                    {step.text}
                  </p>
                </div>
              ))}
            </AnimatedList>
          </AnimatedCard>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            animation: 'fadeInUp 1s ease-out 0.9s both',
          }}>
            <AnimatedButton
              variant="primary"
              onClick={() => setShowSetup(true)}
              style={{
                width: isMobile ? '100%' : undefined,
                boxSizing: 'border-box',
                padding: isMobile ? '1rem 1.5rem' : '1.2rem 3rem',
                fontSize: isMobile ? '1.05rem' : '1.2rem',
              }}
            >
              🎮 {t('home.create_game')}
            </AnimatedButton>

            {hasGames && (
              <AnimatedButton
                onClick={() => { window.location.href = '/games'; }}
                style={{
                  width: isMobile ? '100%' : undefined,
                  boxSizing: 'border-box',
                  padding: isMobile ? '1rem 1.5rem' : '1.2rem 3rem',
                  fontSize: isMobile ? '1.05rem' : '1.2rem',
                }}
              >
                📂 {t('nav.myGames')}
              </AnimatedButton>
            )}
          </div>
        </div>
      ) : (
        /* Game Setup */
        <div style={{
          animation: 'fadeIn 0.5s ease-out',
          padding: isMobile ? '1rem 0.75rem' : '2rem',
        }}>
          <button
            onClick={() => setShowSetup(false)}
            style={{
              padding: '0.75rem 1.5rem',
              marginBottom: '2rem',
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '12px',
              color: '#e2e8f0',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
            }}
          >
            ← {t('common.back')}
          </button>

          <GameSetup />
        </div>
      )}

      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        @keyframes sparkle {
          0%, 100% {
            background-position: 0% 0%;
          }
          50% {
            background-position: 100% 100%;
          }
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 200%; }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow:
              0 0 20px rgba(102, 126, 234, 1),
              0 0 40px rgba(167, 139, 250, 0.8),
              0 0 60px rgba(236, 72, 153, 0.6),
              inset 0 0 20px rgba(167, 139, 250, 0.3);
            border-color: rgba(167, 139, 250, 1);
            filter: brightness(1.5);
          }
          50% {
            box-shadow:
              0 0 30px rgba(236, 72, 153, 1),
              0 0 60px rgba(167, 139, 250, 1),
              0 0 90px rgba(102, 126, 234, 0.8),
              inset 0 0 30px rgba(236, 72, 153, 0.5);
            border-color: rgba(236, 72, 153, 1);
            filter: brightness(2);
          }
        }

      `}</style>
    </div>
  );
}
